import Axios from 'axios';
import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Card, Col, Row, Tooltip, Modal, Typography, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import Vimeo from '@u-wave/react-vimeo';
const { Title } = Typography;

/**
 * トライアルユーザー向け動画視聴ページ
 * キャンペーンに紐づくVideoDeliveryのトークンを使用して動画を表示
 * NavBar表示状態で動作（Auth HOCで認証必須）
 */
function TrialVideoViewPage(props) {
  const [viewModal, setViewModal] = useState(false);
  const [paused, setPaused] = useState(false);
  const [videoId, setVideoId] = useState('');
  const [VDInfo, setVDInfo] = useState({}); // 動画納品情報
  const [videoInfo, setVideoInfo] = useState([]); // 納品対象の動画情報
  const [explText, setExplText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const playerRef = useRef(null);
  const user = useSelector((state) => state.user);

  // 日付フォーマット（yyyy/mm/dd hh:mm）- 視聴期間判定用
  const ymdhms = (tdate) => {
    let date = new Date(tdate);
    return (
      date.getFullYear() +
      '/' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '/' +
      ('0' + date.getDate()).slice(-2) +
      ' ' +
      ('0' + date.getHours()).slice(-2) +
      ':' +
      ('0' + date.getMinutes()).slice(-2)
    );
  };

  // 視聴期間の有効性チェック
  const isValid = (targetDate) => {
    const now = new Date();
    return ymdhms(targetDate) > ymdhms(now);
  };

  // 日付フォーマット（yyyy年mm月dd日）- 表示用
  const ymd = (tdate) => {
    let date = new Date(tdate);
    return (
      date.getFullYear() +
      '年' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '月' +
      ('0' + date.getDate()).slice(-2) +
      '日' +
      '(' +
      ['日', '月', '火', '水', '木', '金', '土'][date.getDay()] +
      ')'
    );
  };

  const readDB = async () => {
    try {
      // ユーザーのキャンペーンキーからvideoDeliveryトークンを取得
      const campaignKey =
        user && user.userData ? user.userData.trialCampaignKey : null;
      if (!campaignKey) {
        setError('動画配信の設定がありません。');
        setLoading(false);
        return;
      }

      const videoRes = await Axios.get(
        `/api/trial/my-video?campaignKey=${encodeURIComponent(campaignKey)}`
      );
      const token = videoRes.data.videoDeliveryToken;
      if (!token) {
        setError('このキャンペーンには動画が設定されていません。');
        setLoading(false);
        return;
      }

      const res = await Axios.get(`/api/videoDelivery/t/${token}`);
      if (res.data.success) {
        let vdInfo = res.data.videoDelivery;

        // 視聴期間チェック
        if (vdInfo.delivery_end && !isValid(vdInfo.delivery_end)) {
          setError('本動画の視聴期限が切れました。');
          setLoading(false);
          return;
        }

        setVDInfo(vdInfo);

        if (vdInfo.delivery_explanation) {
          setExplText(vdInfo.delivery_explanation.replace(/\r?\n/g, '<br>'));
        }

        // 各動画の詳細情報を取得
        const vInfo = [];
        for (let i = 0; i < vdInfo.delivery_video.length; i++) {
          const element = vdInfo.delivery_video[i];
          for (let j = 0; j < element.video_id.length; j++) {
            const video_id = element.video_id[j];
            vInfo.push(
              (await Axios.get(`/api/video/${video_id}`)).data.video
            );
          }
        }
        setVideoInfo(vInfo);
      } else {
        setError('動画情報の取得に失敗しました。');
      }
    } catch (err) {
      console.log(err);
      setError('動画情報の取得中にエラーが発生しました。');
    }
    setLoading(false);
  };

  useEffect(() => {
    // auth完了後にユーザーデータが取得できてから実行
    if (user && user.userData && user.userData.trialCampaignKey) {
      readDB();
    } else if (user && user.userData && !user.userData.trialCampaignKey) {
      // 認証済みだがトライアルユーザーではない
      setError('動画配信の設定がありません。');
      setLoading(false);
    }
  }, [user && user.userData]);

  // モーダルを閉じる処理
  const closeModal = () => {
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.unload();
      } catch (error) {
        console.log('ビデオ停止エラー:', error);
      }
    }
    playerRef.current = null;
    setVideoId('');
    setPaused(false);
    setTimeout(() => {
      setViewModal(false);
    }, 100);
  };

  const handleOk = () => closeModal();
  const handleCancel = () => closeModal();

  // カードクリック時の処理
  const handleCardClick = (v) => {
    setVideoId(v);
    setPaused(false);
    setViewModal(true);
  };

  // Vimeoプレイヤー準備完了
  const handleReady = (player) => {
    playerRef.current = player;
  };

  // ローディング中
  if (loading) {
    return (
      <div className="app" style={{ textAlign: 'center', padding: '3rem' }}>
        <Spin size="large" />
        <p style={{ marginTop: '1rem' }}>読み込み中...</p>
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div className="app">
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Title level={3}>{error}</Title>
        </div>
      </div>
    );
  }

  // 動画表示
  if (VDInfo.delivery_video && videoInfo.length > 0) {
    return (
      <Fragment>
        <div style={{ width: '90%', margin: '1rem auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <Title level={2}>{VDInfo.main_title}</Title>
            {VDInfo.sub_title && (
              <Title level={3}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: `${VDInfo.sub_title}`,
                  }}
                ></div>
              </Title>
            )}
          </div>

          {explText && (
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: `${explText}`,
                }}
              ></div>
            </div>
          )}

          {VDInfo.show_limit === 1 && (
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Title level={4}>
                【視聴期間】　{ymd(VDInfo.delivery_start)}　～
                {ymd(VDInfo.delivery_end)}
              </Title>
            </div>
          )}

          {VDInfo.delivery_video.map((element) => {
            return (
              <Card
                title={element.category}
                styles={{ header: { backgroundColor: 'var(--blue-active)', color: 'var(--white)' }, body: { backgroundColor: 'var(--brand-cream)' } }}
                key={element.category}
                style={{ marginBottom: '2rem' }}
                extra={
                  <Tooltip
                    placement="bottomRight"
                    key={element.category_help}
                    title={element.category_help}
                    color={'var(--brand-primary)'}
                  >
                    {element.category_help && (
                      <QuestionCircleOutlined
                        style={{ fontSize: '25px', color: 'var(--white)' }}
                      />
                    )}
                  </Tooltip>
                }
              >
                <Row gutter={16}>
                  {element.video_id.map((element2) => {
                    const vi = videoInfo.find((item) => item._id === element2);
                    if (!vi) return null;

                    let title = <></>;
                    switch (VDInfo.show_title) {
                      case 0:
                        title = <></>;
                        break;
                      case 1:
                        title = <>{vi.title}</>;
                        break;
                      case 2:
                        title = <>{vi.video_explanation}</>;
                        break;
                      case 3:
                        title = (
                          <>
                            {vi.title}
                            <br />
                            {vi.video_explanation}
                          </>
                        );
                        break;
                      default:
                        title = <></>;
                        break;
                    }

                    // レスポンシブ: モバイルはフル幅で大きく、タブレット 1/2、PC 1/3。
                    // 単独動画は PC で中央寄せ（offset）、モバイルはフル幅（offset なし）。
                    const isSingle = element.video_id.length === 1;
                    const colProps = isSingle
                      ? { xs: { span: 24 }, sm: { span: 8, offset: 8 } }
                      : { xs: 24, sm: 12, md: 8 };

                    return (
                      <Col {...colProps} key={vi._id}>
                        <Card
                          hoverable
                          key={vi._id}
                          style={{ marginBottom: '2rem' }}
                          cover={
                            <div style={{ position: 'relative' }}>
                              <img
                                style={{ width: '100%' }}
                                alt={vi.title}
                                src={vi.thumbnail[0]}
                              />
                              <div className="duration">
                                <span>
                                  {('00' + vi.duration_h).slice(-2)} :{' '}
                                  {('00' + vi.duration_m).slice(-2)} :{' '}
                                  {('00' + vi.duration_s).slice(-2)}
                                </span>
                              </div>
                            </div>
                          }
                          onClick={() =>
                            handleCardClick(vi.url.split('/')[2])
                          }
                        >
                          <div className="video-card-title">{title}</div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            );
          })}

          <br />
        </div>

        {/* 動画再生モーダル */}
        {viewModal && (
          <div>
            <Modal
              title="動画再生"
              open={viewModal}
              onOk={handleOk}
              onCancel={handleCancel}
              styles={{ body: { overflowX: 'scroll' } }}
              width={1000}
              footer={null}
              destroyOnHidden={true}
              maskClosable={false}
            >
              {videoId && (
                <Vimeo
                  key={`vimeo-${videoId}-${Date.now()}`}
                  video={videoId}
                  className="vimeo pc"
                  responsive
                  autoplay={true}
                  speed={true}
                  paused={paused}
                  onReady={handleReady}
                />
              )}
            </Modal>
          </div>
        )}
      </Fragment>
    );
  } else {
    return (
      <div className="app">
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Title level={3}>動画が見つかりません。</Title>
        </div>
      </div>
    );
  }
}

export default TrialVideoViewPage;
